import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Badge } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';

function RescheduleWorkoutModal({ workout, onClose, onSave }) {
  const [phase, setPhase] = useState(workout.effective_phase || '');
  const [week, setWeek] = useState(workout.effective_week || '');
  const [day, setDay] = useState(workout.effective_day || '');
  const [notes, setNotes] = useState(workout.override_notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Calculate new scheduled date preview
  const calculatePreviewDate = () => {
    if (!day || !parseInt(day) || parseInt(day) < 1) return null;

    try {
      // Parse start date (handle both Date objects and strings)
      let startDateStr = workout.program_start_date;
      if (startDateStr instanceof Date) {
        startDateStr = startDateStr.toISOString().split('T')[0];
      } else if (typeof startDateStr === 'string' && startDateStr.includes('T')) {
        startDateStr = startDateStr.split('T')[0];
      }

      // Create date in local timezone
      const startDate = new Date(startDateStr + 'T00:00:00');
      const shiftDays = workout.schedule_shift_days || 0;
      const newDate = new Date(startDate);
      newDate.setDate(newDate.getDate() + (parseInt(day) - 1) + shiftDays);

      return newDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (err) {
      console.error('Error calculating preview date:', err);
      return null;
    }
  };

  const handleSave = async () => {
    setError(null);

    // Validate at least one field changed
    const hasChanges =
      (phase && parseInt(phase) !== workout.effective_phase) ||
      (week && parseInt(week) !== workout.effective_week) ||
      (day && parseInt(day) !== workout.effective_day);

    if (!hasChanges && !notes) {
      setError('Please modify at least one field');
      return;
    }

    // Validate day is required
    if (!day || parseInt(day) < 1) {
      setError('Day number is required and must be at least 1');
      return;
    }

    setIsSaving(true);

    try {
      await workoutAPI.updateWorkoutSchedule(
        workout.active_program_id,
        workout.program_workout_id,
        {
          phase: phase ? parseInt(phase) : null,
          week: week ? parseInt(week) : null,
          day: parseInt(day),
          notes: notes || null
        }
      );
      onSave();
    } catch (err) {
      console.error('Error saving workout schedule:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertToOriginal = async () => {
    if (workout.has_override !== 1) {
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      await workoutAPI.deleteWorkoutScheduleOverride(
        workout.active_program_id,
        workout.program_workout_id
      );
      onSave();
    } catch (err) {
      console.error('Error reverting workout:', err);
      setError('Failed to revert to original schedule.');
    } finally {
      setIsSaving(false);
    }
  };

  const previewDate = calculatePreviewDate();

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Reschedule Workout</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="info" className="mb-3">
          <strong>Program:</strong> {workout.program_name}<br />
          <strong>Workout:</strong> {workout.workout_name}<br />
          {workout.template_name && (
            <>
              <strong>Template:</strong> {workout.template_name}<br />
            </>
          )}
          <strong>Current Scheduled Date:</strong>{' '}
          {(() => {
            // Parse date in local timezone to avoid off-by-one errors
            const dateStr = workout.scheduled_date;
            const date = new Date(dateStr + 'T00:00:00');
            return date.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });
          })()}
          {workout.is_completed === 1 && (
            <>
              <br />
              <Badge bg="success" className="mt-2">
                <i className="fas fa-check-circle me-1"></i>
                Completed
              </Badge>
            </>
          )}
        </Alert>

        {workout.has_override === 1 && (
          <Alert variant="warning">
            <i className="fas fa-info-circle me-2"></i>
            This workout has been rescheduled from its original template values.
          </Alert>
        )}

        <Form>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Phase
                  {workout.template_phase && (
                    <small className="text-muted ms-2">(Original: {workout.template_phase})</small>
                  )}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={phase}
                  onChange={(e) => setPhase(e.target.value)}
                  disabled={isSaving}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Week
                  {workout.template_week && (
                    <small className="text-muted ms-2">(Original: {workout.template_week})</small>
                  )}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={week}
                  onChange={(e) => setWeek(e.target.value)}
                  disabled={isSaving}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Day *
                  {workout.template_day && (
                    <small className="text-muted ms-2">(Original: {workout.template_day})</small>
                  )}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  disabled={isSaving}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {previewDate && (
            <Alert variant="success" className="mb-3">
              <i className="fas fa-calendar-check me-2"></i>
              <strong>New Scheduled Date:</strong> {previewDate}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSaving}
              placeholder="Why are you rescheduling this workout?"
            />
          </Form.Group>

          <Alert variant="secondary">
            <i className="fas fa-lightbulb me-2"></i>
            <strong>Note:</strong> This only changes when THIS workout happens in YOUR copy of the program.
            It does not affect the original program template or other users.
          </Alert>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {workout.has_override === 1 && (
          <Button
            variant="outline-warning"
            onClick={handleRevertToOriginal}
            disabled={isSaving}
            className="me-auto"
          >
            <i className="fas fa-undo me-1"></i>
            Revert to Original
          </Button>
        )}
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <i className="fas fa-spinner fa-spin me-2"></i>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RescheduleWorkoutModal;
